import React, { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import {
  MessageSquare, Clock, Phone, Users, AlertTriangle, Zap
} from "lucide-react";
import { API } from "../App";
import { useTheme } from "../contexts/ThemeContext";
import TopUpDialog from "./usage/TopUpDialog";
import { isIOSApp } from "../hooks/useIsMobileApp";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "");

const UsageCard = ({ compact = false }) => {
  const { isDark } = useTheme();
  const [limits, setLimits] = useState(null);
  const [loading, setLoading] = useState(true);
  const [buyOpen, setBuyOpen] = useState(false);
  const [buyType, setBuyType] = useState(null);

  const getAuthConfig = () => {
    const token = localStorage.getItem("token");
    return { headers: token ? { Authorization: `Bearer ${token}` } : {} };
  };

  useEffect(() => {
    fetchLimits();
  }, []);

  const fetchLimits = async () => {
    try {
      const res = await axios.get(`${API}/usage/limits`, getAuthConfig());
      setLimits(res.data);
    } catch (error) {
      console.error("Failed to fetch usage limits");
    } finally {
      setLoading(false);
    }
  };

  if (loading || !limits) return null;

  const getProgressColor = (used, limit) => {
    if (limit === -1) return "bg-green-500";
    const pct = (used / limit) * 100;
    if (pct >= 100) return "bg-red-500";
    if (pct >= 90) return "bg-orange-500";
    if (pct >= 75) return "bg-yellow-500";
    return "bg-blue-500";
  };

  const getProgressPercent = (used, limit) => {
    if (limit === -1) return 5;
    return Math.min((used / limit) * 100, 100);
  };

  const formatLimit = (limit) => {
    if (limit === -1) return "∞";
    return Number(limit).toLocaleString();
  };

  const openBuy = (type) => { setBuyType(type); setBuyOpen(true); };

  const usageItems = [
    {
      label: "SMS",
      icon: MessageSquare,
      used: limits.limits.sms.used,
      limit: limits.limits.sms.limit,
      unlimited: limits.limits.sms.unlimited,
      topup: limits.limits.sms.topup || 0,
      remaining: limits.limits.sms.remaining,
      buyType: "sms",
      color: "text-purple-400"
    },
    {
      label: "Minutes",
      icon: Clock,
      used: limits.limits.minutes.used,
      limit: limits.limits.minutes.limit,
      unlimited: limits.limits.minutes.unlimited,
      topup: limits.limits.minutes.topup || 0,
      remaining: limits.limits.minutes.remaining,
      buyType: "minutes",
      color: "text-orange-400"
    },
    {
      label: "Lines",
      icon: Phone,
      used: limits.limits.dedicated_lines.used,
      limit: limits.limits.dedicated_lines.limit,
      unlimited: limits.limits.dedicated_lines.unlimited,
      color: "text-cyan-400"
    },
    {
      label: "Team",
      icon: Users,
      used: limits.limits.team_members.used,
      limit: limits.limits.team_members.limit,
      unlimited: limits.limits.team_members.unlimited,
      color: "text-blue-400"
    }
  ];

  if (compact) {
    return (
      <div className="space-y-2">
        {usageItems.map((item, i) => {
          const pct = item.unlimited ? 0 : (item.used / item.limit) * 100;
          const isNearLimit = !item.unlimited && pct >= 75;
          return (
            <div key={i} className="flex items-center gap-2 text-xs">
              <item.icon className={`w-3 h-3 ${item.color}`} />
              <span className="text-muted-foreground w-12">{item.label}</span>
              <div className={`flex-1 h-1.5 rounded-full overflow-hidden ${isDark ? 'bg-white/10' : 'bg-gray-200'}`}>
                <div
                  className={`h-full ${getProgressColor(item.used, item.limit)} transition-all`}
                  style={{ width: `${getProgressPercent(item.used, item.limit)}%` }}
                />
              </div>
              <span className={`w-16 text-right ${isNearLimit ? 'text-yellow-400' : 'text-muted-foreground'}`}>
                {item.used}/{formatLimit(item.limit)}
              </span>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <Card className="glass-card border-theme">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-foreground text-base font-['Manrope']">Plan Usage</CardTitle>
          <Badge className="bg-[#0046BE]/20 text-[#0046BE]">{limits.plan}</Badge>
        </div>
        {limits.billing_period?.start && (
          <p className="text-[11px] text-muted-foreground" data-testid="usage-card-period">
            Billing period {fmtDate(limits.billing_period.start)} – {fmtDate(limits.billing_period.end)} · resets {fmtDate(limits.billing_period.end)}
          </p>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        {usageItems.map((item, i) => {
          const pct = item.unlimited ? 0 : (item.used / item.limit) * 100;
          const isNearLimit = !item.unlimited && pct >= 75;
          const atLimit = !item.unlimited && pct >= 100;

          return (
            <div key={i} className="space-y-1" data-testid={`usage-item-${item.label.toLowerCase()}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <item.icon className={`w-4 h-4 ${item.color}`} />
                  <span className="text-muted-foreground text-sm">{item.label}</span>
                  {item.topup > 0 && (
                    <span className="text-[10px] text-emerald-500">+{formatLimit(item.topup)} top-up</span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {isNearLimit && <AlertTriangle className={`w-3 h-3 ${atLimit ? 'text-red-400' : 'text-yellow-400'}`} />}
                  <span className={`text-sm ${atLimit ? 'text-red-400' : isNearLimit ? 'text-yellow-400' : 'text-muted-foreground'}`}>
                    {Number(item.used).toLocaleString()} / {formatLimit(item.limit)}
                  </span>
                </div>
              </div>
              <div className={`h-2 rounded-full overflow-hidden ${isDark ? 'bg-white/10' : 'bg-gray-200'}`}>
                <div
                  className={`h-full ${getProgressColor(item.used, item.limit)} transition-all`}
                  style={{ width: `${getProgressPercent(item.used, item.limit)}%` }}
                />
              </div>
              {item.buyType && isNearLimit && !isIOSApp() && (
                <Button size="sm" variant="outline" className="h-6 text-[11px] mt-1"
                  onClick={() => openBuy(item.buyType)}
                  data-testid={`usage-buy-${item.buyType}-btn`}>
                  <Zap className="w-3 h-3 mr-1 text-[#FFCC00]" /> Buy More {item.label}
                </Button>
              )}
            </div>
          );
        })}

        {/* Features */}
        <div className={`pt-2 border-t ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
          <p className="text-muted-foreground text-xs mb-2">Features</p>
          <div className="flex flex-wrap gap-2">
            {limits.features.call_masking && (
              <Badge className="bg-purple-500/20 text-purple-400 text-xs">Call Masking</Badge>
            )}
            {limits.features.joby_automation && (
              <Badge className="bg-[#FFCC00]/20 text-[#FFCC00] text-xs">FIELDZ Automation</Badge>
            )}
            {limits.features.chat_support_24_7 && (
              <Badge className="bg-green-500/20 text-green-400 text-xs">24/7 Support</Badge>
            )}
          </div>
        </div>
      </CardContent>
      <TopUpDialog open={buyOpen} onOpenChange={setBuyOpen} usageType={buyType} />
    </Card>
  );
};

export default UsageCard;
